import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const LanguageContext = createContext(null);

const STORAGE_KEY = 'selora_lang';
const SHOW_MEANINGS_KEY = 'selora_show_meanings';

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return 'fr';
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'en' ? 'en' : 'fr';
  });

  // Global "Show English Meaning" toggle for educational content.
  const [showMeanings, setShowMeaningsState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SHOW_MEANINGS_KEY) === '1';
  });

  const setLang = useCallback((next) => {
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_e) { /* noop */ }
  }, []);

  const toggleLang = useCallback(() => {
    setLangState((prev) => {
      const next = prev === 'fr' ? 'en' : 'fr';
      try { localStorage.setItem(STORAGE_KEY, next); } catch (_e) { /* noop */ }
      return next;
    });
  }, []);

  const setShowMeanings = useCallback((v) => {
    setShowMeaningsState(v);
    try { localStorage.setItem(SHOW_MEANINGS_KEY, v ? '1' : '0'); } catch (_e) { /* noop */ }
  }, []);

  const toggleShowMeanings = useCallback(() => {
    setShowMeaningsState((prev) => {
      const next = !prev;
      try { localStorage.setItem(SHOW_MEANINGS_KEY, next ? '1' : '0'); } catch (_e) { /* noop */ }
      return next;
    });
  }, []);

  // Reflect language on <html lang="…"> for accessibility.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  return (
    <LanguageContext.Provider
      value={{ lang, setLang, toggleLang, showMeanings, setShowMeanings, toggleShowMeanings }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Safe fallback if the provider is missing — keeps components rendering.
    return {
      lang: 'fr',
      setLang: () => {},
      toggleLang: () => {},
      showMeanings: false,
      setShowMeanings: () => {},
      toggleShowMeanings: () => {},
    };
  }
  return ctx;
}

/**
 * Resolve a bilingual value.
 *   pick({ fr: 'Bonjour', en: 'Hello' }, 'en') => 'Hello'
 *   pick('Static text', 'en') => 'Static text'
 */
export function pick(value, lang) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (lang === 'en' && value.en != null) return value.en;
    if (value.fr != null) return value.fr;
  }
  return String(value);
}

/**
 * Hook returning a translator function for compact usage:
 *   const t = useT();
 *   t({ fr: 'Bienvenue', en: 'Welcome' })
 */
export function useT() {
  const { lang } = useLang();
  return useCallback((value) => pick(value, lang), [lang]);
}
